import { useMemo, VFC } from 'react'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { useLoginUser } from 'src/api/user'
import { CloseButtonIcon, KeepIcon } from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { TooltipWrapper } from 'src/components/Tooltip'
import { MasterDataWithCategory } from 'src/models/MasterData'
import { PromotionParam } from 'src/models/Promotion'
import { shouldDisplayPaidPlanLabel } from 'src/models/User/role'
import {
  _profilegreen,
  _profilehrborder,
  black,
  defaultShadow,
  lightgray,
  lightgreen,
  orange,
  trueblack,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { flexCenter, leftArrow, lineClamp7 } from 'src/styles/mixins'
import styled, { css, SimpleInterpolation } from 'styled-components'
import { Label } from '../ProfilePreview/styles'
import { FeedbackScore } from '../Rating/FeedbackScore'
import { PrDetailCardProps } from './PrDetailCard'

type ProfileCardProp = {
  user: {
    id: number
    name: string
    companyName: string
    isCertified: boolean
    isActive: boolean
    isNew: boolean
    imageSrc?: string
    position?: string | null
    businessType?: string | null
    businessTypes?: string[]
    numberOfEmployees?: string | null
    prefecture?: string | null
    foundedOn?: string | null
    capital?: string | null
    promotion?: PromotionParam
    kept?: boolean
    adminAges?: string
    feedbackScore?: number
    feedbackCount?: number
  }
  actions: PrDetailCardProps['actions']
  showDetail: (id: number) => void
  showPrDetailModal?: () => void
  isKeep?: boolean
  deleteKeep?: (id: number) => void
  isNeeds?: boolean
  postKeep?: (id: number) => void
  restrictProfile?: boolean
  searchedBusinessTypeIds?: number[]
  businessTypeMasterData?: MasterDataWithCategory[]
}
export const ProfileCard: VFC<ProfileCardProp> = ({
  user,
  actions,
  showDetail,
  showPrDetailModal,
  isKeep,
  deleteKeep,
  isNeeds,
  postKeep,
  restrictProfile,
  searchedBusinessTypeIds,
  businessTypeMasterData,
}) => {
  const { user: loginUser } = useLoginUser()
  const onClickDeleteKeep = async (id: number) => {
    if (deleteKeep) await deleteKeep(id)
  }
  const onClickPostKeep = async (id: number) => {
    if (postKeep) await postKeep(id)
  }

  // 検索条件にマッチする業種を先頭にソート
  const sortedBusinessTypes = useMemo(() => {
    const businessTypes = user.businessTypes
    if (!businessTypes || businessTypes.length === 0) return []
    if (
      !searchedBusinessTypeIds ||
      searchedBusinessTypeIds.length === 0 ||
      !businessTypeMasterData
    )
      return businessTypes

    // マスターデータから業種名→IDのマッピングを作成
    const nameToId: Record<string, number> = {}
    businessTypeMasterData.forEach((item) => {
      nameToId[item.label] = Number(item.value)
    })

    // 検索条件に含まれる業種を先頭に移動
    const matched = businessTypes.filter((name) =>
      searchedBusinessTypeIds.includes(nameToId[name]),
    )
    const unmatched = businessTypes.filter(
      (name) => !searchedBusinessTypeIds.includes(nameToId[name]),
    )
    return [...matched, ...unmatched]
  }, [user.businessTypes, searchedBusinessTypeIds, businessTypeMasterData])
  return (
    <>
      <Card disableMargin={(isKeep || isNeeds) ?? false}>
        <Header displayPrLabel={!!user.promotion}>
          <div>
            <UserIcon
              src={user.imageSrc}
              size="80px"
              hideWithBlur={restrictProfile}
            />
          </div>
          <UserProfile>
            <CompanyName hideWithBlur={restrictProfile}>
              {user.companyName}
            </CompanyName>
            <Name hideWithBlur={restrictProfile}>{user.name}</Name>
            <LabelsDiv>
              {user.position && <Label>{user.position}</Label>}
              {shouldDisplayPaidPlanLabel([loginUser && loginUser.plan.role]) &&
                user.isCertified && (
                  <Label backgroundColor={orange}>有料会員</Label>
                )}
            </LabelsDiv>
            <FeedbackScoreDiv>
              <FeedbackScore
                value={user.feedbackScore}
                feedbackCount={user.feedbackCount}
              />
            </FeedbackScoreDiv>
            <MarkersDiv>
              {user.isActive && (
                <Marker color={lightgreen}>直近アクティブ</Marker>
              )}
              {user.isNew && <Marker>新着企業</Marker>}
            </MarkersDiv>
          </UserProfile>
          {user.promotion && (
            <Promotion>
              <PromotionText>PR</PromotionText>
            </Promotion>
          )}
          {isKeep && (
            <Keep>
              <KeepContain>
                <StyledCloseButtonIcon
                  onClick={() => onClickDeleteKeep(user.id)}
                />
              </KeepContain>
            </Keep>
          )}
          {isNeeds && (
            <Needs>
              <NeedsContain>
                <KeepButton
                  kept={user.kept ? true : false}
                  onClick={() => {
                    if (user.kept) onClickDeleteKeep(user.id)
                    else onClickPostKeep(user.id)
                  }}
                >
                  <KeepIcon />
                  <span>{user.kept ? '保存済み' : '保存する'}</span>
                </KeepButton>
              </NeedsContain>
            </Needs>
          )}
        </Header>
        <Content>
          {user.promotion?.displayType === 'free_text' ? (
            <PrDetail>
              <button onClick={showPrDetailModal}>
                <p>{user.promotion.prDetail}</p>
                <div>クリックで全文表示</div>
              </button>
            </PrDetail>
          ) : (
            <CompanyInfo>
              <div>
                <p>業種</p>
                <BusinessTypeDisplay>
                  {sortedBusinessTypes.length > 0 ? (
                    <>
                      <span>{sortedBusinessTypes[0]}</span>
                      {sortedBusinessTypes.length > 1 && (
                        <TooltipWrapper
                          tooltipNode={
                            <BusinessTypeTooltipContent>
                              {sortedBusinessTypes.map((type, index) => (
                                <div key={index}>{type}</div>
                              ))}
                            </BusinessTypeTooltipContent>
                          }
                          tooltipStyle={businessTypeTooltipStyle}
                          direction={{ x: 'right', y: 'bottom' }}
                          xValue="0"
                          yValue="100%"
                          writingMode="horizontal-tb"
                        >
                          <BusinessTypeCount>
                            +{sortedBusinessTypes.length - 1}
                          </BusinessTypeCount>
                        </TooltipWrapper>
                      )}
                    </>
                  ) : (
                    <span>{user.businessType}</span>
                  )}
                </BusinessTypeDisplay>
              </div>
              <div>
                <p>従業員数</p>
                <p>{user.numberOfEmployees}</p>
              </div>
              <div>
                <p>所在地</p>
                <p>{user.prefecture}</p>
              </div>
              <div>
                <p>設立年数</p>
                {user.foundedOn && <p>{user.foundedOn} 年</p>}
              </div>
              <div>
                <p>資本金</p>
                <p>{user.capital}</p>
              </div>
              <div>
                <p>決裁者年齢</p>
                <p>{user.adminAges}</p>
              </div>
            </CompanyInfo>
          )}
          <DetailButton onClick={() => showDetail(user.id)}>
            詳細プロフィールを見る
          </DetailButton>
          <Buttons>
            {actions.map(({ label, onClick, styles, hidden, disabled }) => (
              <Button
                key={label}
                onClick={() => onClick(user.id)}
                styles={styles}
                hidden={typeof hidden === 'function' ? hidden(user.id) : hidden}
                disabled={
                  typeof disabled === 'function' ? disabled(user.id) : disabled
                }
                numOfSiblings={actions.length}
              >
                {label}
              </Button>
            ))}
          </Buttons>
        </Content>
      </Card>
    </>
  )
}

const Card = styled.div<{ disableMargin: boolean }>`
  margin: 0 5%;
  filter: drop-shadow(${defaultShadow});
  padding: 20px 0;
  position: relative;

  :hover {
    z-index: 1;
  }

  ${({ disableMargin }) =>
    disableMargin &&
    css`
      margin: 0;
    `}
`

const Header = styled.div<{ displayPrLabel: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 15px;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  background-color: ${black};
  color: ${truewhite};
  font-size: 12px;
  min-height: 150px;

  ${({ displayPrLabel }) =>
    displayPrLabel &&
    css`
      color: ${trueblack};
      background-color: #ffff99;
    `}

  > div:first-child {
    margin: 0 5%;
    ${flexCenter};
  }

  p {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin: 2px 0;
  }
  ${Label} {
    font-size: 12px;
    width: fit-content;
  }
`
const Promotion = styled.div`
  height: 100%;
  width: 66px;
`
const PromotionText = styled.div`
  font-size: 12px;
  width: 66px;
  padding: 6px 24px;
  text-align: center;
  background-color: #ffffff;
`

const Keep = styled.div`
  height: 100%;
  width: 66px;
`

const KeepContain = styled.div`
  display: flex;
  justify-content: flex-end;
  transform: translate(10px, -8px);
`

const StyledCloseButtonIcon = styled(CloseButtonIcon)`
  cursor: pointer;
  width: 22px;
  height: 22px;
`

const Needs = styled.div`
  height: 100%;
  width: 66px;
`

const NeedsContain = styled.div``
const KeepButton = styled.button<{ kept: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  border-radius: 25px;
  background-color: #ff6c00;
  position: absolute;
  top: 40px;
  z-index: 1;
  min-width: 70px;
  font-size: 12px;
  color: #ffffff;
  border: 1px solid #ff6c00;
  box-shadow: 0 3px 6px rgb(0 0 0 / 16%);
  font-weight: bold;
  svg {
    margin-right: 3px;
    fill: #ffffff;
  }
  ${({ kept }) =>
    !kept &&
    css`
      background-color: #fff;
      color: #838383;
      border: 1px solid #838383;
      svg {
        margin-right: 3px;
        fill: #838383;
      }
    `}
`

const CompanyName = styled.p<{
  hideWithBlur?: boolean
}>`
  font-weight: ${fontWeightBold};
  ${({ hideWithBlur }) =>
    hideWithBlur &&
    css`
      filter: blur(2.5px);
    `}
`
const Name = styled.p<{
  hideWithBlur?: boolean
}>`
  font-size: 16px;
  font-weight: ${fontWeightBold};
  ${({ hideWithBlur }) =>
    hideWithBlur &&
    css`
      filter: blur(2.5px);
    `}
`
const LabelsDiv = styled.div`
  display: flex;
`
const MarkersDiv = styled.div``
const Marker = styled.p`
  font-size: 10px;
  display: flex;
  align-items: center;
  ::before {
    content: '';
    display: block;
    background-color: ${lightgreen};
    border-radius: 50%;
    width: 10px;
    height: 10px;
    margin-right: 3px;
  }
`
const UserProfile = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 10%;
  width: calc(100% - 80px - 66px - 10%);
  height: 100%;
  > ${MarkersDiv} {
    border-top: 1px dotted;
    margin-top: 5px;
    padding-top: 10px;
  }
`
const Content = styled.div`
  padding: 40px 20px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  background-color: ${truewhite};
`
const PrDetail = styled.div`
  button {
    margin-bottom: 20px;
    width: 300px;

    p {
      white-space: pre-wrap;

      font-size: 14px;
      line-height: 20px;
      ${lineClamp7}
    }

    div {
      font-size: 12px;
      color: ${lightgray};
      margin-top: 6px;
      text-align: center;
    }
  }
`
const CompanyInfo = styled.div`
  width: 100%;
  > div {
    display: flex;
    max-width: 260px;
    margin: 0 auto 20px;
    p {
      :nth-child(1) {
        flex: 2;
        font-weight: ${fontWeightBold};
      }
      :nth-child(2) {
        flex: 3;
      }
    }
  }
`

const BusinessTypeDisplay = styled.div`
  flex: 3;
  display: flex;
  align-items: center;
  gap: 8px;
`

const BusinessTypeCount = styled.span`
  color: #02b7b0;
  font-size: 12px;
  font-weight: ${fontWeightBold};
  cursor: pointer;
  white-space: nowrap;
`

const BusinessTypeTooltipContent = styled.div`
  > div {
    padding: 2px 0;
    white-space: nowrap;
  }
`

const businessTypeTooltipStyle = css`
  padding: 8px 12px;
  font-size: 12px;
  z-index: 10;
`

const Buttons = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  margin: 20px 0;
  padding-top: 20px;
  width: 100%;
  border-top: 1px solid ${_profilehrborder};
  > * {
    margin-top: 20px;
  }
`
const ButtonBase = styled.button`
  min-width: 145px;
  max-width: 145px;
  min-height: 45px;
  max-height: 45px;
  margin-top: 0px;
  padding: 10px 0;
  border-radius: 5px;

  font-size: 14px;
  font-weight: ${fontWeightBold};
  text-align: center;
  :disabled {
    opacity: 0.5;
  }
`
export const Button = styled(ButtonBase)<{
  styles: SimpleInterpolation
  numOfSiblings: number
}>`
  width: ${({ numOfSiblings }) => `${80 / numOfSiblings}%`};
  ${({ styles }) => styles}
`

const DetailButton = styled.button`
  color: ${_profilegreen};
  border: 1px solid;
  border-radius: 5px;
  padding: 10px 40px 10px 20px;
  font-weight: ${fontWeightBold};

  ${leftArrow};
  ::after {
    border-width: 2px;
    top: -5%;
  }
  :focus,
  :hover {
    color: ${truewhite};
    background-color: ${_profilegreen};
  }
`

const FeedbackScoreDiv = styled.div`
  padding-top: 4px;
  padding-bottom: 4px;
`
